'use client';

/* eslint-disable @next/next/no-img-element */
import Link from 'next/link';
import type { CSSProperties } from 'react';
import type { LearningModuleDto, SkillType } from '@vuganawe/types';
import { useT } from '@/i18n/client';
import { SKILL_COLORS, SKILL_GLYPHS, formatMinutes } from '@/lib/learning';

/**
 * Course-cover art. Uses the uploaded cover image when the course has one,
 * otherwise falls back to a generated two-tone gradient in the skill's accent
 * color with a large typographic glyph — so cards always look intentional.
 */
export function CourseCover({
  skill,
  imageUrl,
  compact,
}: {
  skill: SkillType;
  imageUrl?: string | null;
  compact?: boolean;
}) {
  const t = useT();
  const colors = SKILL_COLORS[skill];
  if (imageUrl) {
    return (
      <div className={`vn-cover vn-cover--image${compact ? ' vn-cover--compact' : ''}`} aria-hidden="true">
        <img src={imageUrl} alt="" className="vn-cover__img" />
        <span className="vn-cover__skill">{t(`skills.${skill}`)}</span>
      </div>
    );
  }
  return (
    <div
      className={`vn-cover${compact ? ' vn-cover--compact' : ''}`}
      style={{ '--cover-base': colors.base, '--cover-deep': colors.deep } as CSSProperties}
      aria-hidden="true"
    >
      <span className="vn-cover__glyph">{SKILL_GLYPHS[skill]}</span>
      <span className="vn-cover__skill">{t(`skills.${skill}`)}</span>
    </div>
  );
}

/**
 * Catalog-style course card (cover, title, summary, meta, progress) used on
 * /learn, the learner dashboard, and anywhere courses are listed.
 */
export function CourseCard({ module, hrefBase = '/learn' }: { module: LearningModuleDto; hrefBase?: string }) {
  const t = useT();
  const started = module.progressPercent > 0;
  return (
    <Link href={`${hrefBase}/${module.slug}`} className="vn-cc">
      <CourseCover skill={module.skill} imageUrl={module.imageUrl} />
      <div className="vn-cc__body">
        <h3 className="vn-cc__title">{module.title}</h3>
        <p className="vn-cc__summary">{module.summary}</p>
        <p className="vn-cc__meta">
          <span className="vn-cc__level">{t(`levels.${module.level}`)}</span>
          {module.ratingCount > 0 && module.averageRating !== null ? (
            <span className="vn-cc__stars">★ {module.averageRating}</span>
          ) : null}
          <span>
            {t(module.lessonCount === 1 ? 'common.lesson' : 'common.lessons', {
              count: module.lessonCount,
            })}
          </span>
          <span>{formatMinutes(module.totalDurationMinutes)}</span>
          {module.learnerCount > 0 ? (
            <span>
              {t(module.learnerCount === 1 ? 'common.learner' : 'common.learners', {
                count: module.learnerCount,
              })}
            </span>
          ) : null}
        </p>
        {started ? (
          <div className="vn-cc__progress">
            <div className="vn-progress">
              <div className="vn-progress__bar" style={{ width: `${module.progressPercent}%` }} />
            </div>
            <span className="vn-cc__progress-label">
              {t('card.done', { completed: module.completedLessonCount, total: module.lessonCount })}
            </span>
          </div>
        ) : null}
        <span className="vn-cc__cta">
          {started ? t('card.continueLearning') : t('card.startCourse')} →
        </span>
      </div>
    </Link>
  );
}
